import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { fetchServiceabilityWithDetails } from '@/apiCalls/fetchServicing';
import { getQuickQuote } from '@/apiCalls/matchingEngine';
import { DisplayButtons } from '@/components/DisplayButtons';
import {
    selectBillsPaidFrequency,
    selectEducation,
    selectExpensesShared,
    selectGeneral,
    selectHealthcare,
    selectMortgageRent,
    selectOtherLoans,
    selectTransportVehicle,
} from '@/context/borrower';
import {
    EDisplays,
    setDisplay,
    setServiceabilityResult,
} from '@/context/display';
import { selectLenders } from '@/context/lender';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';

interface IExpenseButtonsProps {
    setShowErrors: (value: boolean) => void;
    setLoading: (value: boolean) => void;
    baseId: string;
}

export function ExpenseButtons({
    setShowErrors,
    setLoading,
    baseId,
}: IExpenseButtonsProps): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const shareExpenses = useAppSelector(selectExpensesShared);
    const billsFrequency = useAppSelector(selectBillsPaidFrequency);
    const generalLiving = useAppSelector(selectGeneral);
    const mortgageRent = useAppSelector(selectMortgageRent);
    const transportVehicle = useAppSelector(selectTransportVehicle);
    const education = useAppSelector(selectEducation);
    const healthcare = useAppSelector(selectHealthcare);
    const otherLoans = useAppSelector(selectOtherLoans);
    const { technicalFailureScreen } = useCopy();

    // ***** Event Handlers *****
    function handleBackClick(): void {
        const backScreen = getBackScreen();
        dispatch(setDisplay(backScreen));
    }

    async function handleNextClick(): Promise<void> {
        setShowErrors(true);
        if (!isComplete()) return;
        createOrUpdateBorrower();
        const nextScreen = await getNextScreen();
        dispatch(setDisplay(nextScreen));
    }

    // ***** Helpers ******
    function isComplete(): boolean {
        if (shareExpenses === undefined) return false;
        if (billsFrequency === undefined) return false;
        if (generalLiving === undefined) return false;
        if (mortgageRent === undefined) return false;
        if (transportVehicle === undefined) return false;
        if (education === undefined) return false;
        if (healthcare === undefined) return false;
        if (otherLoans === undefined) return false;
        return true;
    }

    async function getNextScreen(): Promise<EDisplays> {
        setLoading(true);
        try {
            // 1. fetch serviceability result
            const serviceabilityResult = await fetchServiceabilityWithDetails();

            if (!serviceabilityResult.isServicing) {
                // 2. if not servicing, return failure
                return EDisplays.SERVICING_FAILURE;
            }

            // 3. if servicing, fetch lenders
            await getQuickQuote();

            // No wait here. getQuickQuote awaits the request and dispatches
            // the lenders before it resolves, so awaiting it is already
            // enough — the second this bought was spent on every customer
            // leaving this screen, and a slower quote would have run past it
            // anyway and been read as no lenders.

            // 5. get lenders result
            const state = store.getState();
            const lenders = selectLenders(state);

            const hasLenders = lenders && lenders.length > 0;

            // 6. store result in state
            dispatch(
                setServiceabilityResult({
                    isServicing: true,
                    hasLenders: hasLenders,
                    lenders: serviceabilityResult.lenders,
                })
            );

            // 7. return success
            return EDisplays.SERVICING_SUCCESS;
        } catch (error) {
            console.error('Serviceability check failed:', error);
            // The request never completed, so there is no affordability
            // answer. SERVICING_FAILURE tells the customer their income may
            // not be high enough, which is not something we know here.
            return technicalFailureScreen
                ? EDisplays.SERVICING_TECHNICAL_FAILURE
                : EDisplays.SERVICING_FAILURE;
        } finally {
            setLoading(false);
        }
    }

    function getBackScreen(): EDisplays {
        return EDisplays.INCOME;
    }

    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
        />
    );
}
